import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ShieldAlert, ShieldCheck, KeyRound, Webhook, PowerOff, Power } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 6.1 — Shopify Admin Console
 *
 * Read-only visibility into:
 *   - Integration feature-flag state (from env)
 *   - Credential slot presence
 *   - Admin API token cache freshness
 *   - Storefront token status
 *   - Last webhook received
 *   - Webhook event ledger
 *
 * Action buttons:
 *   - Invalidate admin token cache (force refresh via client-credentials)
 *   - Replay webhook registration (re-run webhookSubscriptionCreate for all topics)
 *
 * All calls hit /api/admin/shopify/*.
 */
export default function ShopifyAdmin() {
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState({ total: 0, events: [] });
  const [busy, setBusy] = useState(false);

  const loadHealth = () =>
    api.get("/admin/shopify/health").then((r) => setHealth(r.data)).catch((e) =>
      toast.error(formatApiError(e.response?.data?.detail) || e.message));

  const loadEvents = () =>
    api.get("/admin/shopify/webhook-events?limit=25").then((r) => setEvents(r.data)).catch((e) =>
      toast.error(formatApiError(e.response?.data?.detail) || e.message));

  useEffect(() => { loadHealth(); loadEvents(); }, []);

  const invalidateToken = async () => {
    setBusy(true);
    try {
      await api.post("/admin/shopify/token/invalidate");
      toast.success("Admin token cache invalidated. Next call will refresh via client-credentials.");
      await loadHealth();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const replayStartup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/shopify/startup/replay");
      const okCount = (r.data.webhooks || []).filter((w) => w.status === "registered" || w.status === "already_registered").length;
      toast.success(`Webhook registration replay: ${okCount}/${(r.data.webhooks || []).length} topics OK`);
      await loadHealth();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!health) {
    return <div className="text-[var(--smoke)] text-sm" data-testid="shopify-admin-loading">Loading…</div>;
  }

  const enabled = health.integration_enabled;
  const creds = health.credentials_present || {};
  const missingCreds = Object.entries(creds).filter(([, v]) => !v).map(([k]) => k);

  return (
    <div className="space-y-6" data-testid="shopify-admin-tab">
      {/* Status banner */}
      <div
        data-testid="shopify-status-banner"
        className={
          "rounded-2xl border-2 border-[var(--ink)] p-5 flex items-start gap-4 shadow-[4px_4px_0_var(--ink)] " +
          (enabled ? "bg-[#C5A059]/10 border-[#C5A059]" : "bg-white")
        }
      >
        {enabled ? (
          <ShieldCheck className="text-[#C5A059] shrink-0 mt-1" size={22} />
        ) : (
          <PowerOff className="text-[var(--smoke)] shrink-0 mt-1" size={22} />
        )}
        <div className="flex-1">
          <div className="uppercase tracking-widest text-xs font-bold text-[var(--ink)]">
            Shopify integration
          </div>
          <div className="mt-1 text-[var(--ink)] font-bold text-lg">
            {enabled ? "ENABLED (listen-only)" : "DISABLED (SHOPIFY_INTEGRATION_ENABLED=false)"}
          </div>
          <div className="mt-2 text-[var(--smoke)] text-sm">
            {enabled
              ? "Backend fetches admin tokens via client-credentials grant, verifies webhook HMACs, and registers subscriptions. No user-facing UI is exposed until SHOPIFY_ORDERS_UI_ENABLED=true (Phase 6.2)."
              : "All Shopify code is inert. Flip the master flag in backend/.env to activate."}
          </div>
        </div>
      </div>

      {/* Credentials */}
      <section>
        <h2 className="font-display text-xl mb-3 inline-flex items-center gap-2">
          <KeyRound size={18} className="text-[var(--panini-red)]" /> Credentials
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(creds).map(([k, v]) => (
            <div
              key={k}
              data-testid={`shopify-cred-${k}`}
              className={
                "rounded-xl border-2 border-[var(--ink)] p-3 " +
                (v ? "bg-white" : "bg-[var(--panini-yellow-soft)]")
              }
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                {k.replaceAll("_", " ")}
              </div>
              <div className={"mt-1 font-bold text-sm " + (v ? "text-[var(--ink)]" : "text-[var(--panini-red)]")}>
                {v ? "SET" : "MISSING"}
              </div>
            </div>
          ))}
        </div>
        {missingCreds.length > 0 && (
          <div
            data-testid="shopify-cred-warning"
            className="mt-3 rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] p-4 text-sm text-[var(--ink)] flex items-start gap-2"
          >
            <ShieldAlert size={16} className="text-[var(--panini-red)] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">Missing: {missingCreds.join(", ")}</div>
              <div className="text-[var(--smoke)] mt-1">
                See <code>/app/memory/PHASE_6_DEV_DASHBOARD_WALKTHROUGH.md</code> for the click-by-click steps.
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Token caches */}
      <section>
        <h2 className="font-display text-xl mb-3">Token caches</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <TokenCard
            title="Admin API token"
            testid="shopify-admin-token-card"
            has={health.admin_token_cache?.has_token}
            expires={health.admin_token_cache?.expires_at}
            fetched={health.admin_token_cache?.fetched_at}
            note="Fetched dynamically via client-credentials grant. TTL 24 h. Encrypted at rest."
            action={
              <button
                type="button"
                onClick={invalidateToken}
                disabled={busy}
                data-testid="shopify-invalidate-token"
                className="btn-ghost text-xs"
              >
                <RefreshCw size={12} /> Invalidate
              </button>
            }
          />
          <TokenCard
            title="Storefront public token"
            testid="shopify-storefront-token-card"
            has={health.storefront_token_cache?.has_token}
            expires={null}
            fetched={health.storefront_token_cache?.created_at}
            note="Created via storefrontAccessTokenCreate (Q11=A). Long-lived. Public-safe."
          />
        </div>
      </section>

      {/* Webhook ledger */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-xl inline-flex items-center gap-2">
            <Webhook size={18} className="text-[var(--panini-red)]" /> Webhook events
          </h2>
          <div className="flex items-center gap-2">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]" data-testid="shopify-events-count">
              {events.total} received
            </div>
            <button
              type="button"
              onClick={loadEvents}
              className="btn-ghost text-xs"
              data-testid="shopify-events-refresh"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              type="button"
              onClick={replayStartup}
              disabled={busy || !enabled}
              className="btn-red text-xs"
              data-testid="shopify-replay-startup"
              title={enabled ? "Re-run webhookSubscriptionCreate for all topics" : "Enable integration first"}
            >
              <Power size={12} /> Replay registration
            </button>
          </div>
        </div>
        {events.events.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[var(--ink)]/40 bg-white p-6 text-[var(--smoke)] text-center text-sm"
                data-testid="shopify-events-empty">
            No webhook events recorded yet.
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--cream)] border-b-2 border-[var(--ink)]">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Topic</th>
                  <th className="text-left px-4 py-2 font-bold">Webhook ID</th>
                  <th className="text-left px-4 py-2 font-bold">Received</th>
                  <th className="text-left px-4 py-2 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {events.events.map((e) => (
                  <tr key={`${e.topic}-${e.webhook_id}`} className="border-t border-[var(--ink)]/10" data-testid={`shopify-event-${e.webhook_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{e.topic}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[var(--smoke)]">{e.webhook_id}</td>
                    <td className="px-4 py-2 text-xs text-[var(--smoke)]">{e.received_at}</td>
                    <td className="px-4 py-2 text-xs font-bold">{e.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function TokenCard({ title, testid, has, expires, fetched, note, action }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[3px_3px_0_var(--ink)]"
    >
      <div className="flex items-baseline justify-between">
        <div className="font-bold text-[var(--ink)]">{title}</div>
        <div className={"text-[11px] font-bold uppercase tracking-widest " + (has ? "text-[#C5A059]" : "text-[var(--smoke)]")}>
          {has ? "CACHED" : "EMPTY"}
        </div>
      </div>
      {has && expires && (
        <div className="text-xs text-[var(--smoke)] mt-2">
          Expires: <span className="font-mono">{expires}</span>
        </div>
      )}
      {has && fetched && (
        <div className="text-xs text-[var(--smoke)] mt-1">
          Fetched: <span className="font-mono">{fetched}</span>
        </div>
      )}
      <div className="text-xs text-[var(--smoke)] mt-2">{note}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
